import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatStepper, MatStepperModule } from "@angular/material/stepper";
import { Guid } from "guid-typescript";
import { ProjectService } from "../../../../../common/services/project.service";
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { CreateDescriptionReport, DescriptionReport, Project, ProjectActivity, UpdateDescriptionReport } from "../../../../../common/models/project";
import { MatCardModule } from "@angular/material/card";
import { MatSelectModule } from "@angular/material/select";
import { ActivitiesComponent } from "./activity/activities.component";
import { FileUploadComponent } from '../../../../../common/components/file/file.component';
import { AssociationService } from '../../../../../common/services/association.service';
import { StorageService } from '../../../../../common/services/storage.service';
import { CurrentUser } from '../../../../../common/models/user';
import { Association } from '../../../../../common/models/association';
import { FormattedNumberInputDirective } from '../../../../../common/directives/inputs/formatted-number.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { EMPTY, Observable, Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
    selector: 'app-description',
    templateUrl: './description.component.html',
    styleUrls: ['./description.component.scss'],
    imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    ActivitiesComponent,
    FileUploadComponent,
    FormattedNumberInputDirective
]
})
  export class DescriptionComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    
    isHandset: boolean = false;

    descriptionReport: DescriptionReport | null = null;

    projectId?: Guid;
    reportId?: Guid;

    isSaving: boolean = false;
    isEditMode: boolean = false;
    isLoading: boolean = true;

    user: CurrentUser | null = null;

    
    // Define FormGroups for each step
    generalInfoProjectDetailFormGroup: FormGroup;
    generalInfoContactDetailFormGroup: FormGroup;
    achievementsFormGroup: FormGroup;
    executorsFormGroup: FormGroup;
    budgetFormGroup: FormGroup;
    additionalDataFormGroup: FormGroup;
    activitiesFormGroup: FormGroup;
    attachmentsSummaryFormGroup: FormGroup;

    private readonly snackbarConfig: MatSnackBarConfig = {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top'
    };
    
  
    constructor(
      private fb: FormBuilder,
      private storageService: StorageService,
      private associationService: AssociationService,
      private projectService: ProjectService,
      private snackBar: MatSnackBar,
      private route: ActivatedRoute,
      private router: Router,
      private location: Location,
      private breakpointObserver: BreakpointObserver
    ) {
      // Initialize FormGroups with controls
      this.generalInfoProjectDetailFormGroup = this.fb.group({
        reportingPeriodStart: [null],
        reportingPeriodEnd: [null],
        contractClass: [null],
        organizationName: [null],
        programName: [null],
        authorizedPerson: [null],
        programManager: [null],
        implementationLocation: [null],
        approvedAmount: [0],
        spentAmount: [0]
      });

      this.generalInfoContactDetailFormGroup = this.fb.group({
        organizationAddress: [null],
        organizationPhone: [null],
        organizationFax: [null],
        contactPerson: [null],
        contactPhone: [null],
        contactEmail: [null],
        website: [null],
      });
  
      this.achievementsFormGroup = this.fb.group({
        achievementDescription: [null],
        userImpactDescription: [null],
        numberOfUsers: [null],
        deviationReason: [null],
        projectObstacles: [null],
      });
  
      this.executorsFormGroup = this.fb.group({
        numberOfEmployees: [null],
        numberOfVolunteers: [null],
        newEmployees: [false],
        volunteerActivities: [null],
        personnelChangesReason: [null],
      });
  
      this.budgetFormGroup = this.fb.group({
        deviationFromPlannedBudget: [null],
        spendingChallenges: [null],
        coFinancingDetails: [null],
      });
  
      this.additionalDataFormGroup = this.fb.group({
        successStory: [null],
        mediaCollaborations: [null],
        obstaclesAndLessons: [null],
        plannedActivitiesForNextPeriod: [null],
      });

      this.activitiesFormGroup = this.fb.group({
         activities: this.fb.array([]),
      });

      this.attachmentsSummaryFormGroup = this.fb.group({
        attachmentsSummary: [null],
        fileStorageLocation: [`descriptionreports/${Guid.create().toString()}`]
      });
  
    }
  
    ngOnInit(): void {
      
        this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          this.isHandset = result.matches;
        });

        // Extract projectId and reportId from route parameters
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const projectIdParam = params.get('projectId');
            const reportIdParam = params.get('reportId');

            // Validate and assign projectId
            if (projectIdParam && Guid.isGuid(projectIdParam)) {
                this.projectId = Guid.parse(projectIdParam);

                // Check if reportId is present for editing an existing report
                if (reportIdParam && Guid.isGuid(reportIdParam)) {
                    this.reportId = Guid.parse(reportIdParam);

                    this.isLoading = true;
                    this.isEditMode = true;

                     this.loadDescriptionReport$().pipe(
                        finalize(() => this.isLoading = false),
                        takeUntil(this.destroy$)
                    ).subscribe({
                        next: () => {
                        },
                        error: (err) => {
                            console.error('Error loading description report', err);
                        }
                    });

                } else {
                  this.isEditMode = false; // Set insert mode
                  this.isLoading = false;

                     // In create mode, once user is loaded, we autopopulate
                    this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
                      this.user = user;
                      if (this.user && !this.isEditMode) {

                        this.patchFieldsFromUser();

                        if (this.projectId) {
                          this.projectService.getProject(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
                            next: (project) => this.patchFieldsFromProject(project),
                            error: (err) => console.error('Error fetching project data', err)
                          });
                        }
                        
                        if (this.user.associationId) {
                          this.associationService.getAssociation(this.user.associationId).pipe(takeUntil(this.destroy$)).subscribe({
                            next: (association) => this.patchFieldsFromAssociation(association),
                            error: (err) => console.error('Error fetching association data', err)
                          });
                        }                
                      }
                    });                    
                }
            } else {
                // Handle missing or invalid projectId
                console.error('Invalid or missing project id.');
                this.router.navigate(['association', 'projects']);
            }
        });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    private populateActivitiesForm(activities?: ProjectActivity[]): void {
        const fb = this.fb;
        
        const activityControls = (activities ?? []).map(activity => fb.group({
          id: [activity.id ?? ''],
          name: [activity?.name || '', [Validators.required, Validators.maxLength(100)]],
          description: [activity?.description || '', [Validators.required, Validators.maxLength(1000)]],
          startDate: [activity?.startDate || null, Validators.required],
          endDate: [activity?.endDate || null, Validators.required],
          results: [activity?.results || '', [Validators.required, Validators.maxLength(1000)]]
        }));
  
      this.activitiesFormGroup.setControl('activities', fb.array(activityControls));
    }

    private patchFieldsFromUser(): void {
      if (!this.user) return;
  
      this.generalInfoProjectDetailFormGroup.patchValue({
        authorizedPerson: `${this.user.firstName} ${this.user.lastName}`,
        programManager: `${this.user.firstName} ${this.user.lastName}`
      });
  
      this.generalInfoContactDetailFormGroup.patchValue({
        contactPerson: `${this.user.firstName} ${this.user.lastName}`,
        contactEmail: this.user.email
      });
    }

    private patchFieldsFromProject(project: Project): void {
      this.generalInfoProjectDetailFormGroup.patchValue({
        approvedAmount: project.approvedAmount,
        programName: project.name,
        // contractClass: project.contractClass
      });
    }
  
    private patchFieldsFromAssociation(association: Association): void {
      this.generalInfoProjectDetailFormGroup.patchValue({
        organizationName: association.name,
        implementationLocation: `${this.user?.municipalityName}, ${association.postalCodeAndHeadquarters}, ${association.county}` 
      });
  
      this.generalInfoContactDetailFormGroup.patchValue({
        organizationAddress: `${association.address}, ${association.postalCodeAndHeadquarters}, ${this.user?.municipalityName}`  ,
        website: association.web,
        contactPhone: association.contactNumber
      });
    }
   
    private loadDescriptionReport$(): Observable<DescriptionReport> {
        if (!this.projectId || !this.reportId) {
            return EMPTY;
        }

        return this.projectService.getDescriptionReport(this.projectId, this.reportId).pipe(
            tap(report => {
                this.descriptionReport = report;
                this.populateDescriptionReport(this.descriptionReport);
            }),
            catchError(err => {
                console.error('Error fetching description report', err);
                return EMPTY;
            }),
            takeUntil(this.destroy$)
        );
    }
  
    private populateDescriptionReport(report: DescriptionReport): void {
      this.generalInfoProjectDetailFormGroup.patchValue({
        reportingPeriodStart: report.reportingPeriodStart,
        reportingPeriodEnd: report.reportingPeriodEnd,
        contractClass: report.contractClass,
        organizationName: report.organizationName,
        programName: report.programName,
        authorizedPerson: report.authorizedPerson,
        programManager: report.programManager,
        implementationLocation: report.implementationLocation,
        approvedAmount: report.approvedAmount,
        spentAmount: report.spentAmount
      });

      this.generalInfoContactDetailFormGroup.patchValue({
        organizationAddress: report.organizationAddress,
        organizationPhone: report.organizationPhone,
        organizationFax: report.organizationFax,
        contactPerson: report.contactPerson,
        contactPhone: report.contactPhone,
        contactEmail: report.contactEmail,
        website: report.website
      })
  
      this.achievementsFormGroup.patchValue({
        achievementDescription: report.achievementDescription,
        userImpactDescription: report.userImpactDescription,
        numberOfUsers: report.numberOfUsers,
        deviationReason: report.deviationReason,
        projectObstacles: report.projectObstacles,
      });
  
      this.executorsFormGroup.patchValue({
        numberOfEmployees: report.numberOfEmployees,
        numberOfVolunteers: report.numberOfVolunteers,
        newEmployees: report.newEmployees,
        volunteerActivities: report.volunteerActivities,
        personnelChangesReason: report.personnelChangesReason,
      });
  
      this.budgetFormGroup.patchValue({
        deviationFromPlannedBudget: report.deviationFromPlannedBudget,
        spendingChallenges: report.spendingChallenges,
        coFinancingDetails: report.coFinancingDetails,
      });
  
      this.additionalDataFormGroup.patchValue({
        successStory: report.successStory,
        mediaCollaborations: report.mediaCollaborations,
        obstaclesAndLessons: report.obstaclesAndLessons,
        plannedActivitiesForNextPeriod: report.plannedActivitiesForNextPeriod,
      });

      this.attachmentsSummaryFormGroup.patchValue({
        attachmentsSummary: report.attachmentsSummary,
        fileStorageLocation: report.fileStorageLocation
      });

      this.populateActivitiesForm(report.activities);
  
    }

    private buildActivities(): ProjectActivity[] {
      const raw = this.activitiesFormGroup.getRawValue() as any;
      
      return (raw?.activities ?? []).map((activity: any) => ({
        id: activity?.id ? Guid.parse(activity.id).toString() : Guid.EMPTY.toString(),
        name: activity?.name ?? '',
        description: activity?.description ?? '',
        startDate: activity?.startDate,
        endDate: activity?.endDate,
        results: activity?.results ?? ''
      }));
    }
  
    onSubmit(stepper: MatStepper): void {
      if (this.isEditMode) {
        this.updateDescriptionReport(stepper);
      } else {
        this.createDescriptionReport(stepper);
      }
    }
  
    createDescriptionReport(stepper: MatStepper): void {
        if (!this.projectId) {
            console.log('Project id is missing in createDescriptionReport');
            return;
        }

      const createDto: CreateDescriptionReport = {
        ...this.generalInfoProjectDetailFormGroup.value,
        ...this.generalInfoContactDetailFormGroup.value,
        ...this.achievementsFormGroup.value,
        ...this.executorsFormGroup.value,
        ...this.budgetFormGroup.value,
        ...this.additionalDataFormGroup.value,
        ...this.attachmentsSummaryFormGroup.value,
        activities: this.buildActivities()
      };
  
      this.projectService.createDescriptionReport(this.projectId, createDto).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Opisni izvještaj uspješno kreiran', 'Zatvori', this.snackbarConfig);
          stepper.reset();
          this.router.navigate(['association', 'projects', this.projectId!.toString(), 'reports']);
        },
        error: (error) => {
          this.snackBar.open('Greška prilikom kreiranja opisnog izvještaja', 'Zatvori', this.snackbarConfig);
          console.error(error);
        }
      });
    }
  
    updateDescriptionReport(stepper: MatStepper): void {
      if (!this.projectId) {
        console.log('Project id is missing in updateDescriptionReport.');
        return;
      }
      if (!this.reportId) {
        console.error('Report id is missing in updateDescriptionReport.');
        return;
      }
    
      const updateDto: UpdateDescriptionReport = {
        id: this.reportId,
        ...this.generalInfoProjectDetailFormGroup.value,
        ...this.generalInfoContactDetailFormGroup.value,
        ...this.achievementsFormGroup.value,
        ...this.executorsFormGroup.value,
        ...this.budgetFormGroup.value,
        ...this.additionalDataFormGroup.value,
        ...this.attachmentsSummaryFormGroup.value,
        activities: this.buildActivities()
      };
  
      this.projectService.updateDescriptionReport(this.projectId, this.reportId, updateDto).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Opisni izvještaj uspješno ažuriran', 'Zatvori', this.snackbarConfig);
          stepper.reset();
          this.router.navigate(['association', 'projects', this.projectId!.toString(), 'reports']);
        },
        error: (error) => {
          this.snackBar.open('Greška prilikom ažuriranja opisnog izvještaja', 'Zatvori', this.snackbarConfig);
          console.error(error);
        }
      });
    } 
    
    saveDescriptionReport(): void {
        if (this.isSaving) {
           return;
        }
    
        this.isSaving = true;
    
        const formsToValidate = [
          this.generalInfoProjectDetailFormGroup,
          this.generalInfoContactDetailFormGroup,
          this.achievementsFormGroup,
          this.executorsFormGroup,
          this.budgetFormGroup,
          this.additionalDataFormGroup,
          this.activitiesFormGroup,
          this.attachmentsSummaryFormGroup
        ];
    
        const invalidForms = formsToValidate.filter(form => form && form.invalid);
        
        if (invalidForms.length > 0) {
          // Mark all fields as touched to show validation errors
          formsToValidate.forEach(form => form?.markAllAsTouched());
          
          this.snackBar.open('Molimo ispravno ispuniti sva obavezna polja prije spremanja', 'Zatvori', this.snackbarConfig);
          this.isSaving = false;
          return;
        }
    
        if (this.isEditMode) {
          // Edit mode - call update endpoint
          if (!this.projectId || !this.reportId) {
            console.error('Project ID or Report ID is missing');
            this.isSaving = false;
            return;
          }

          const updateDto: UpdateDescriptionReport = {
            id: this.reportId,
            ...this.generalInfoProjectDetailFormGroup.value,
            ...this.generalInfoContactDetailFormGroup.value,
            ...this.achievementsFormGroup.value,
            ...this.executorsFormGroup.value,
            ...this.budgetFormGroup.value,
            ...this.additionalDataFormGroup.value,
            ...this.attachmentsSummaryFormGroup.value,
            activities: this.buildActivities()
          };
    
          this.projectService.updateDescriptionReport(this.projectId, this.reportId, updateDto).pipe(
            switchMap(() => this.loadDescriptionReport$()),
            finalize(() => this.isSaving = false),
            takeUntil(this.destroy$)
          ).subscribe({
            next: () => this.snackBar.open('Opisni izvještaj uspješno ažuriran', 'Zatvori', this.snackbarConfig),
            error: (error) => this.snackBar.open('Greška prilikom spremanja promjena', 'Zatvori', this.snackbarConfig)
          });

        } else {
    
          if (!this.projectId) {
            console.error('Project ID is missing');
            this.isSaving = false;
            return;
          }
    
          // Create mode - call create endpoint and transform to edit
          const createDto: CreateDescriptionReport = {
            ...this.generalInfoProjectDetailFormGroup.value,
            ...this.generalInfoContactDetailFormGroup.value,
            ...this.achievementsFormGroup.value,
            ...this.executorsFormGroup.value,
            ...this.budgetFormGroup.value,
            ...this.additionalDataFormGroup.value,
            ...this.attachmentsSummaryFormGroup.value,
            activities: this.buildActivities()
          };
    
          this.projectService.createDescriptionReport(this.projectId, createDto).pipe(
            tap(reportId => {
              this.reportId = Guid.parse(reportId);
              this.isEditMode = true;
            }),
            switchMap(() => this.loadDescriptionReport$()),
            finalize(() => this.isSaving = false),
            takeUntil(this.destroy$)
          ).subscribe({
            next: () => {
              // Update URL
              const newUrl = this.router.createUrlTree(['association', 'projects', this.projectId!.toString(), 'reports', 'description', this.reportId!.toString(), 'edit']);
              this.location.replaceState(this.router.serializeUrl(newUrl));
              
              this.snackBar.open('Opisni izvještaj uspješno kreiran', 'Zatvori', this.snackbarConfig);
            },
            error: (error) => this.snackBar.open('Greška prilikom kreiranja opisnog izvještaja', 'Zatvori', this.snackbarConfig)
          });
         
        }
    }
 
  }
